import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import SectionNavPills from '../components/SectionNavPills'
import PromoBanner from '../components/PromoBanner'
import { trackEvent } from '../lib/analytics'

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL

const Pricing = () => {
  const navigate = useNavigate()
  const { user, session, hasPaidAccess } = useAuth() // Use canonical access
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [ageConfirmed, setAgeConfirmed] = useState(false) // 18+ confirmation checkbox
  
  // Phase 2: Billing form state
  const [showBillingForm, setShowBillingForm] = useState(false)
  const [billingData, setBillingData] = useState({
    customer_type: 'individual',
    invoice_name: '',
    billing_email: user?.email || '',
    street_address: '',
    postal_code: '',
    city: '',
    country: 'Slovensko',
    phone: '',
    ico: '',
    dic: '',
    ic_dph: '',
    electronic_invoice_consent: true,
    postal_invoice_name: '',
    postal_invoice_street: '',
    postal_invoice_postal_code: '',
    postal_invoice_city: '',
    postal_invoice_country: 'Slovensko'
  })
  const [billingErrors, setBillingErrors] = useState({})

  // Bank-transfer feature: fetched from backend so the option only appears
  // when Stripe Dashboard side is actually enabled (see /api/checkout/config).
  const [paymentMethod, setPaymentMethod] = useState('card') // 'card' | 'bank_transfer'
  const [bankTransferEnabled, setBankTransferEnabled] = useState(false)
  // UX-only state: holds the backend-generated Stripe Checkout URL while we
  // show the Slovak explanatory confirmation BEFORE opening Stripe. The URL
  // itself is the exact one the backend returned — we never create a second
  // Checkout Session for the user's click.
  const [bankTransferConfirm, setBankTransferConfirm] = useState({ show: false, url: '' })

  useEffect(() => {
    let cancelled = false
    fetch(`${BACKEND_URL}/api/checkout/config`)
      .then((r) => (r.ok ? r.json() : null))
      .then((cfg) => {
        if (!cancelled && cfg) setBankTransferEnabled(!!cfg.bank_transfer_enabled)
      })
      .catch(() => {})
    return () => { cancelled = true }
  }, [])

  // Fire pricing_viewed once on mount (analytics helper silently no-ops
  // until the visitor grants consent).
  useEffect(() => {
    trackEvent('pricing_viewed')
  }, [])

  const handlePurchase = async () => {
    console.log('🔴 PURCHASE CLICKED - Handler started')
    console.log('User:', user)
    console.log('Session:', session)
    trackEvent('checkout_started', { package: 'individual' })
    
    if (!user) {
      console.log('⚠️ No user - redirecting to login')
      navigate('/login', { state: { from: '/pricing' } })
      return
    }

    console.log('✅ User authenticated')
    
    // Phase 2: Show billing form first
    if (!showBillingForm) {
      setShowBillingForm(true)
      // Pre-fill email
      setBillingData(prev => ({ ...prev, billing_email: user.email || '' }))
      return
    }
    
    // If billing form is shown, validate and proceed with checkout
    await handleBillingSubmit()
  }
  
  const validateBillingData = () => {
    const errors = {}
    
    if (!billingData.invoice_name.trim()) {
      errors.invoice_name = 'Meno je povinné'
    }
    if (!billingData.billing_email.trim()) {
      errors.billing_email = 'Email je povinný'
    }
    if (!billingData.street_address.trim()) {
      errors.street_address = 'Adresa je povinná'
    }
    if (!billingData.postal_code.trim()) {
      errors.postal_code = 'PSČ je povinné'
    }
    if (!billingData.city.trim()) {
      errors.city = 'Mesto je povinné'
    }
    
    // Company-specific validation
    if (billingData.customer_type === 'company' && !billingData.ico.trim()) {
      errors.ico = 'IČO je povinné pre firmu'
    }
    
    // Postal invoice validation (if consent is OFF)
    if (!billingData.electronic_invoice_consent) {
      if (!billingData.postal_invoice_name.trim()) {
        errors.postal_invoice_name = 'Meno pre poštovú faktúru je povinné'
      }
      if (!billingData.postal_invoice_street.trim()) {
        errors.postal_invoice_street = 'Adresa pre poštovú faktúru je povinná'
      }
      if (!billingData.postal_invoice_postal_code.trim()) {
        errors.postal_invoice_postal_code = 'PSČ pre poštovú faktúru je povinné'
      }
      if (!billingData.postal_invoice_city.trim()) {
        errors.postal_invoice_city = 'Mesto pre poštovú faktúru je povinné'
      }
    }
    
    setBillingErrors(errors)
    return Object.keys(errors).length === 0
  }
  
  const handleBillingSubmit = async () => {
    console.log('✅ User authenticated, proceeding with checkout')

    if (!validateBillingData()) {
      setError('Prosím, vyplňte všetky povinné polia')
      return
    }

    try {
      setLoading(true)
      setError('')
      
      const originUrl = window.location.origin
      const payload = {
        origin_url: originUrl,
        billing_data: {
          customer_type: billingData.customer_type,
          invoice_name: billingData.invoice_name,
          billing_email: billingData.billing_email,
          street_address: billingData.street_address,
          postal_code: billingData.postal_code,
          city: billingData.city,
          country: billingData.country,
          phone: billingData.phone || null,
          ico: billingData.customer_type === 'company' ? billingData.ico : null,
          dic: billingData.customer_type === 'company' ? (billingData.dic || null) : null,
          ic_dph: billingData.customer_type === 'company' ? (billingData.ic_dph || null) : null
        },
        electronic_invoice_consent: billingData.electronic_invoice_consent,
        postal_invoice_address: !billingData.electronic_invoice_consent ? {
          name: billingData.postal_invoice_name,
          street: billingData.postal_invoice_street,
          postal_code: billingData.postal_invoice_postal_code,
          city: billingData.postal_invoice_city,
          country: billingData.postal_invoice_country
        } : null,
        metadata: {
          source: 'pricing_page'
        },
        payment_method: paymentMethod
      }

      const response = await fetch(`${BACKEND_URL}/api/checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token}`
        },
        body: JSON.stringify(payload)
      })

      let data
      try {
        data = await response.json()
      } catch (parseError) {
        console.error('Failed to parse response:', parseError)
        throw new Error('Neplatná odpoveď zo servera')
      }

      if (!response.ok) {
        throw new Error(data?.detail || data?.errors?.[0] || 'Chyba pri vytváraní platby')
      }

      if (data.url) {
        // Bank-transfer flow: show Slovak explanatory modal first; let the
        // user's own click open Stripe in a new tab (so popup blockers do
        // not intercept it). The URL is the exact one the backend returned
        // — we do NOT create a second Checkout Session.
        if (paymentMethod === 'bank_transfer') {
          setBankTransferConfirm({ show: true, url: data.url })
        } else {
          window.location.href = data.url
        }
      } else {
        throw new Error('Chýba URL pre platbu')
      }

    } catch (err) {
      console.error('Purchase error:', err)
      setError(err?.message || 'Nastala chyba pri spracovaní platby')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <SectionNavPills />
      <div className="py-14 md:py-20">
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mx-auto">
          <header className="mb-6 md:mb-8 text-center">
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-3">Cenník</h1>
            <p className="text-sm sm:text-base text-gray-600 leading-relaxed max-w-2xl mx-auto">
              Získajte prístup ku všetkým zverejneným testom v rámci školského roka.
            </p>
          </header>

          {/* Calm value card — matches polished EduMisia homepage card system. */}
          <div className="mb-10 rounded-2xl bg-white border border-gray-100 ring-1 ring-gray-50 shadow-sm p-6 sm:p-8 md:p-10" data-testid="pricing-value-card">
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 text-center mb-3">
              Všetko potrebné v jednom balíčku
            </h2>
            <p className="text-sm sm:text-base text-gray-600 text-center max-w-2xl mx-auto mb-6 sm:mb-8">
              Odomknite všetky testy, výsledky a vysvetlenia v jednom balíčku.
            </p>

            <div className="grid sm:grid-cols-3 gap-3 sm:gap-4">
              <div className="rounded-2xl bg-white border border-gray-100 ring-1 ring-gray-50 shadow-sm p-4 sm:p-5">
                <div className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-gradient-to-br from-emerald-500 to-green-600 text-white flex items-center justify-center shadow-md shadow-emerald-500/25 ring-4 ring-emerald-50">
                    <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                    </svg>
                  </span>
                  <div className="min-w-0">
                    <h3 className="font-semibold text-gray-900 text-sm sm:text-base mb-1">Všetky testy</h3>
                    <p className="text-xs sm:text-sm text-gray-600">Online testy sprístupňované počas školského roka.</p>
                  </div>
                </div>
              </div>

              <div className="rounded-2xl bg-white border border-gray-100 ring-1 ring-gray-50 shadow-sm p-4 sm:p-5">
                <div className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-gradient-to-br from-blue-600 to-indigo-600 text-white flex items-center justify-center shadow-md shadow-blue-500/25 ring-4 ring-blue-50">
                    <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6h13M9 6l-3 3m3-3l-3-3M5 13H3m2 0v6m0-6V7" />
                    </svg>
                  </span>
                  <div className="min-w-0">
                    <h3 className="font-semibold text-gray-900 text-sm sm:text-base mb-1">Okamžité výsledky</h3>
                    <p className="text-xs sm:text-sm text-gray-600">Detailné vyhodnotenie a štatistiky.</p>
                  </div>
                </div>
              </div>

              <div className="rounded-2xl bg-white border border-gray-100 ring-1 ring-gray-50 shadow-sm p-4 sm:p-5">
                <div className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-gradient-to-br from-amber-500 to-orange-500 text-white flex items-center justify-center shadow-md shadow-amber-500/25 ring-4 ring-amber-50">
                    <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                    </svg>
                  </span>
                  <div className="min-w-0">
                    <h3 className="font-semibold text-gray-900 text-sm sm:text-base mb-1">Vysvetlenia</h3>
                    <p className="text-xs sm:text-sm text-gray-600">Nápovedy ku každej otázke.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Pricing card */}
          <div className="bg-white rounded-2xl border border-gray-100 ring-1 ring-gray-50 shadow-sm overflow-hidden" data-testid="pricing-card">
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-6 sm:p-8 text-center text-white">
              <h2 className="text-xl sm:text-2xl font-bold mb-1">Program EduMisia</h2>
              <p className="text-blue-100 text-sm sm:text-base">Kompletný balíček pre školský rok</p>
            </div>

            {/* Price Display */}
            <div className="p-6 sm:p-8 md:p-10">
              <PromoBanner kind="individual" className="mb-6" />
              <div className="text-center mb-6 sm:mb-8 pb-6 sm:pb-8 border-b border-gray-200">
                <div className="inline-flex items-baseline gap-2">
                  <span className="text-5xl sm:text-6xl font-extrabold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">14,99 €</span>
                  <span className="text-gray-600 text-base sm:text-lg">/ školský rok</span>
                </div>
                <p className="text-gray-600 mt-2 text-sm sm:text-base">
                  Prístup do 31. mája daného školského roka
                </p>
              </div>

            {/* Features */}
            <div className="space-y-4 mb-8">
              <div className="flex items-start gap-3" data-testid="feature-tests">
                <svg className="w-6 h-6 text-green-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <div>
                  <h3 className="font-semibold">Online testy sprístupňované počas školského roka</h3>
                  <p className="text-gray-600 text-sm">Prístup do 31. mája daného školského roka</p>
                </div>
              </div>

              <div className="flex items-start gap-3" data-testid="feature-attempts">
                <svg className="w-6 h-6 text-green-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <div>
                  <h3 className="font-semibold">Neobmedzené pokusy</h3>
                  <p className="text-gray-600 text-sm">Môžete riešiť každý test viackrát</p>
                </div>
              </div>

              <div className="flex items-start gap-3" data-testid="feature-instant">
                <svg className="w-6 h-6 text-green-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <div>
                  <h3 className="font-semibold">Okamžité vyhodnotenie</h3>
                  <p className="text-gray-600 text-sm">Výsledky hneď po dokončení testu</p>
                </div>
              </div>

              <div className="flex items-start gap-3" data-testid="feature-explanations">
                <svg className="w-6 h-6 text-green-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <div>
                  <h3 className="font-semibold">Nápovedy a vysvetlenia</h3>
                  <p className="text-gray-600 text-sm">Jednoduché vysvetlenia ku každej otázke</p>
                </div>
              </div>

              <div className="flex items-start gap-3" data-testid="feature-ranking">
                <svg className="w-6 h-6 text-green-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <div>
                  <h3 className="font-semibold">Porovnanie s ostatnými</h3>
                  <p className="text-gray-600 text-sm">Anonymný rebríček a percentily</p>
                </div>
              </div>

              <div className="flex items-start gap-3" data-testid="feature-progressive">
                <svg className="w-6 h-6 text-green-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <div>
                  <h3 className="font-semibold">Postupné odomykanie</h3>
                  <p className="text-gray-600 text-sm">Testy sa odomykajú podľa školského roka</p>
                </div>
              </div>
            </div>

            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg" data-testid="error-message">
                <p className="text-red-600 text-sm">{error}</p>
              </div>
            )}

            {/* Phase 2: Billing Form (shown only after first click on purchase) */}
            {showBillingForm && !(user && hasPaidAccess) && (
              <div className="mb-6 p-6 bg-blue-50 border border-blue-200 rounded-xl" data-testid="billing-form">
                <h3 className="text-xl font-bold text-gray-900 mb-1">Fakturačné údaje</h3>
                <p className="text-sm text-gray-600 mb-6">
                  Tieto údaje použijeme na vystavenie faktúry. Polia označené <span className="text-red-500">*</span> sú povinné.
                </p>

                {/* Customer Type */}
                <div className="mb-5">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Typ zákazníka <span className="text-red-500">*</span>
                  </label>
                  <div className="flex gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="customer_type"
                        value="individual"
                        checked={billingData.customer_type === 'individual'}
                        onChange={(e) => setBillingData({ ...billingData, customer_type: e.target.value })}
                        className="h-4 w-4 text-blue-600"
                        data-testid="customer-type-individual"
                      />
                      <span className="text-sm text-gray-700">Fyzická osoba</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="customer_type"
                        value="company"
                        checked={billingData.customer_type === 'company'}
                        onChange={(e) => setBillingData({ ...billingData, customer_type: e.target.value })}
                        className="h-4 w-4 text-blue-600"
                        data-testid="customer-type-company"
                      />
                      <span className="text-sm text-gray-700">Firma / organizácia</span>
                    </label>
                  </div>
                </div>

                {/* Invoice Name */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {billingData.customer_type === 'company' ? 'Názov firmy / organizácie' : 'Meno a priezvisko'} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={billingData.invoice_name}
                    onChange={(e) => setBillingData({ ...billingData, invoice_name: e.target.value })}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${billingErrors.invoice_name ? 'border-red-500' : 'border-gray-300'}`}
                    placeholder={billingData.customer_type === 'company' ? 'napr. ABC s.r.o.' : 'napr. Jana Nováková'}
                    data-testid="billing-invoice-name"
                  />
                  {billingErrors.invoice_name && <p className="text-red-500 text-xs mt-1">{billingErrors.invoice_name}</p>}
                </div>

                {/* Billing Email */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Fakturačný e-mail <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    value={billingData.billing_email}
                    onChange={(e) => setBillingData({ ...billingData, billing_email: e.target.value })}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${billingErrors.billing_email ? 'border-red-500' : 'border-gray-300'}`}
                    placeholder="napr. jana@email.sk"
                    data-testid="billing-email"
                  />
                  {billingErrors.billing_email && <p className="text-red-500 text-xs mt-1">{billingErrors.billing_email}</p>}
                </div>

                {/* Street Address */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Ulica a číslo <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={billingData.street_address}
                    onChange={(e) => setBillingData({ ...billingData, street_address: e.target.value })}
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${billingErrors.street_address ? 'border-red-500' : 'border-gray-300'}`}
                    placeholder="napr. Hlavná 123"
                    data-testid="billing-street"
                  />
                  {billingErrors.street_address && <p className="text-red-500 text-xs mt-1">{billingErrors.street_address}</p>}
                </div>

                {/* PSČ + City */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      PSČ <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={billingData.postal_code}
                      onChange={(e) => setBillingData({ ...billingData, postal_code: e.target.value })}
                      className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${billingErrors.postal_code ? 'border-red-500' : 'border-gray-300'}`}
                      placeholder="napr. 811 01"
                      data-testid="billing-postal-code"
                    />
                    {billingErrors.postal_code && <p className="text-red-500 text-xs mt-1">{billingErrors.postal_code}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Mesto <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={billingData.city}
                      onChange={(e) => setBillingData({ ...billingData, city: e.target.value })}
                      className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${billingErrors.city ? 'border-red-500' : 'border-gray-300'}`}
                      placeholder="napr. Bratislava"
                      data-testid="billing-city"
                    />
                    {billingErrors.city && <p className="text-red-500 text-xs mt-1">{billingErrors.city}</p>}
                  </div>
                </div>

                {/* Country + Phone */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Krajina
                    </label>
                    <input
                      type="text"
                      value={billingData.country}
                      onChange={(e) => setBillingData({ ...billingData, country: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      data-testid="billing-country"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Telefón
                    </label>
                    <input
                      type="tel"
                      value={billingData.phone}
                      onChange={(e) => setBillingData({ ...billingData, phone: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="napr. +421 900 123 456"
                      data-testid="billing-phone"
                    />
                  </div>
                </div>

                {/* Company-specific fields */}
                {billingData.customer_type === 'company' && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4 p-4 bg-white border border-blue-200 rounded-lg">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        IČO <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={billingData.ico}
                        onChange={(e) => setBillingData({ ...billingData, ico: e.target.value })}
                        className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${billingErrors.ico ? 'border-red-500' : 'border-gray-300'}`}
                        placeholder="12345678"
                        data-testid="billing-ico"
                      />
                      {billingErrors.ico && <p className="text-red-500 text-xs mt-1">{billingErrors.ico}</p>}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        DIČ
                      </label>
                      <input
                        type="text"
                        value={billingData.dic}
                        onChange={(e) => setBillingData({ ...billingData, dic: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="2021234567"
                        data-testid="billing-dic"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        IČ DPH
                      </label>
                      <input
                        type="text"
                        value={billingData.ic_dph}
                        onChange={(e) => setBillingData({ ...billingData, ic_dph: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="SK2021234567"
                        data-testid="billing-ic-dph"
                      />
                    </div>
                  </div>
                )}

                {/* Electronic Invoice Consent */}
                <div className="mb-4 mt-6 p-4 bg-white border border-gray-200 rounded-lg">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={billingData.electronic_invoice_consent}
                      onChange={(e) => setBillingData({ ...billingData, electronic_invoice_consent: e.target.checked })}
                      className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      data-testid="billing-electronic-consent"
                    />
                    <span className="text-sm text-gray-700">
                      Súhlasím so zaslaním faktúry v elektronickej forme na uvedený e-mail.
                    </span>
                  </label>
                </div>

                {/* Postal Invoice Fallback Fields */}
                {!billingData.electronic_invoice_consent && (
                  <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-lg" data-testid="postal-invoice-section">
                    <h4 className="font-semibold text-gray-900 mb-3">Adresa pre poštové doručenie faktúry</h4>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Meno príjemcu <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={billingData.postal_invoice_name}
                          onChange={(e) => setBillingData({ ...billingData, postal_invoice_name: e.target.value })}
                          className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${billingErrors.postal_invoice_name ? 'border-red-500' : 'border-gray-300'}`}
                          data-testid="postal-invoice-name"
                        />
                        {billingErrors.postal_invoice_name && <p className="text-red-500 text-xs mt-1">{billingErrors.postal_invoice_name}</p>}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Ulica a číslo <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={billingData.postal_invoice_street}
                          onChange={(e) => setBillingData({ ...billingData, postal_invoice_street: e.target.value })}
                          className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${billingErrors.postal_invoice_street ? 'border-red-500' : 'border-gray-300'}`}
                          data-testid="postal-invoice-street"
                        />
                        {billingErrors.postal_invoice_street && <p className="text-red-500 text-xs mt-1">{billingErrors.postal_invoice_street}</p>}
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            PSČ <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            value={billingData.postal_invoice_postal_code}
                            onChange={(e) => setBillingData({ ...billingData, postal_invoice_postal_code: e.target.value })}
                            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${billingErrors.postal_invoice_postal_code ? 'border-red-500' : 'border-gray-300'}`}
                            data-testid="postal-invoice-postal-code"
                          />
                          {billingErrors.postal_invoice_postal_code && <p className="text-red-500 text-xs mt-1">{billingErrors.postal_invoice_postal_code}</p>}
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Mesto <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            value={billingData.postal_invoice_city}
                            onChange={(e) => setBillingData({ ...billingData, postal_invoice_city: e.target.value })}
                            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${billingErrors.postal_invoice_city ? 'border-red-500' : 'border-gray-300'}`}
                            data-testid="postal-invoice-city"
                          />
                          {billingErrors.postal_invoice_city && <p className="text-red-500 text-xs mt-1">{billingErrors.postal_invoice_city}</p>}
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Krajina
                        </label>
                        <input
                          type="text"
                          value={billingData.postal_invoice_country}
                          onChange={(e) => setBillingData({ ...billingData, postal_invoice_country: e.target.value })}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          data-testid="postal-invoice-country"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Age Requirement Notice */}
            {!(user && hasPaidAccess) && (
              <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 mt-0.5">
                    <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <p className="text-sm text-gray-700">
                    <strong>Upozornenie:</strong> Balíček môže zakúpiť iba osoba staršia ako 18 rokov, 
                    alebo rodič či iný zákonný zástupca maloletého používateľa.
                  </p>
                </div>
              </div>
            )}

            {/* Age Confirmation Checkbox */}
            {!(user && hasPaidAccess) && (
              <div className="mb-6">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={ageConfirmed}
                    onChange={(e) => setAgeConfirmed(e.target.checked)}
                    className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="text-sm text-gray-700">
                    Potvrdzujem, že som osoba staršia ako 18 rokov, alebo rodič či iný zákonný 
                    zástupca maloletého používateľa.
                  </span>
                </label>
              </div>
            )}

            {/* School Usage Restrictions Notice + School Usage Terms Confirmation
                were intentionally removed from this regular-test purchase flow.
                They belong only to the school FG purchase flow (SchoolPurchase.js). */}

            {/* Access Status or Purchase Button */}
            {user && hasPaidAccess ? (
              <div className="text-center py-6" data-testid="access-active">
                <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-emerald-100 text-emerald-600 inline-flex items-center justify-center">
                  <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold text-emerald-700 mb-2">Máte aktívny prístup!</h3>
                <p className="text-gray-600 mb-6">
                  Všetky testy sú odomknuté a pripravené na riešenie.
                </p>
                <button
                  onClick={() => navigate('/dashboard')}
                  className="w-full px-8 py-4 bg-emerald-600 text-white rounded-xl font-semibold text-lg hover:bg-emerald-700 transition shadow-md shadow-emerald-500/25"
                  data-testid="go-to-dashboard-button"
                >
                  Prejsť na testy
                </button>
              </div>
            ) : (
              <>
                {/* Payment method selector — appears only when bank transfer
                    is enabled globally (via /api/checkout/config) AND the user
                    has opened the billing form. Card stays the default. */}
                {showBillingForm && bankTransferEnabled && (
                  <div
                    className="mb-4 rounded-2xl border border-slate-200 bg-white p-4"
                    data-testid="payment-method-selector"
                  >
                    <div className="text-sm font-semibold text-gray-900 mb-2">Spôsob platby</div>
                    <label className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition ${paymentMethod === 'card' ? 'border-blue-500 ring-2 ring-blue-100 bg-blue-50/40' : 'border-slate-200 hover:border-slate-300'}`}>
                      <input
                        type="radio"
                        name="pm"
                        className="mt-1"
                        checked={paymentMethod === 'card'}
                        onChange={() => setPaymentMethod('card')}
                        data-testid="pm-card-radio"
                      />
                      <div>
                        <div className="text-sm font-semibold text-gray-900">Platba kartou</div>
                        <div className="text-xs text-gray-600 mt-0.5">
                          Prístup k testom sa aktivuje spravidla ihneď po úspešnej platbe.
                        </div>
                      </div>
                    </label>
                    <label className={`mt-2 flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition ${paymentMethod === 'bank_transfer' ? 'border-blue-500 ring-2 ring-blue-100 bg-blue-50/40' : 'border-slate-200 hover:border-slate-300'}`}>
                      <input
                        type="radio"
                        name="pm"
                        className="mt-1"
                        checked={paymentMethod === 'bank_transfer'}
                        onChange={() => setPaymentMethod('bank_transfer')}
                        data-testid="pm-bank-transfer-radio"
                      />
                      <div>
                        <div className="text-sm font-semibold text-gray-900">Bankový prevod</div>
                        <div className="text-xs text-gray-600 mt-0.5">
                          Prístup k testom aktivujeme po pripísaní a potvrdení platby.
                        </div>
                      </div>
                    </label>
                  </div>
                )}

                <button
                  onClick={(e) => {
                    console.log('🔵 Button onClick fired', e)
                    handlePurchase()
                  }}
                  disabled={loading || !ageConfirmed}
                  className="w-full px-8 py-4 bg-orange-500 text-white rounded-xl font-bold text-lg shadow-md shadow-orange-500/25 hover:bg-orange-600 hover:shadow-lg hover:shadow-orange-500/30 hover:-translate-y-0.5 active:translate-y-0 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                  data-testid="purchase-button"
                >
                  {loading
                    ? 'Pripravujem platbu...'
                    : !user
                      ? 'Prihlásiť sa a zakúpiť'
                      : showBillingForm
                        ? 'Pokračovať na platbu'
                        : 'Zakúpiť prístup'}
                </button>

                {showBillingForm && (
                  <button
                    type="button"
                    onClick={() => setShowBillingForm(false)}
                    disabled={loading}
                    className="w-full mt-2 px-8 py-2 text-sm text-gray-600 hover:text-gray-800 transition disabled:opacity-50"
                    data-testid="billing-back-button"
                  >
                    ← Späť
                  </button>
                )}

                {!ageConfirmed && (
                  <p className="text-center text-sm text-amber-600 mt-2">
                    Pre pokračovanie prosím potvrďte vekové obmedzenie
                  </p>
                )}

                {!user && (
                  <p className="text-center text-sm text-gray-500 mt-4">
                    Pred zakúpením sa musíte prihlásiť
                  </p>
                )}
              </>
            )}
            </div>
          </div>

          {/* Info Section */}
          <div className="mt-12 text-center text-gray-600">
            <p className="mb-4">
              <strong>Bezpečná platba cez Stripe</strong>
            </p>
            <p className="text-sm">
              Po úspešnej platbe získate okamžitý prístup ku všetkým už zverejneným testom.
              Ďalšie testy sa sprístupňujú postupne počas školského roka. Prístup k balíčku
              máte do 31. mája daného školského roka, takže sa k testom môžete vracať aj po 
              ukončení hlavnej časti Testovania 9.
            </p>
          </div>
        </div>
      </div>
      </div>

      {/* Bank-transfer confirmation modal — UX only.
          Shown AFTER the backend has returned a valid Stripe Checkout URL
          and ONLY when paymentMethod === 'bank_transfer'. The "Pokračovať"
          link opens that exact backend-returned URL in a new tab from a
          direct user gesture (popup-blocker safe). No second Checkout
          Session is ever created. The original EduMisia tab stays open. */}
      {bankTransferConfirm.show && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 p-4 sm:p-6"
          role="dialog"
          aria-modal="true"
          aria-labelledby="bank-transfer-modal-heading"
          data-testid="bank-transfer-modal"
        >
          <div className="w-full sm:max-w-lg bg-white rounded-2xl shadow-xl max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="flex items-start gap-3 px-5 py-4 sm:px-6 sm:py-5 border-b border-gray-100">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 21h18M4 10h16M5 21V10m14 11V10M8 10V7l4-3 4 3v3M9 14h.01M12 14h.01M15 14h.01M9 18h.01M12 18h.01M15 18h.01" />
                </svg>
              </div>
              <div className="flex-1">
                <h2 id="bank-transfer-modal-heading" className="text-lg sm:text-xl font-bold text-gray-900">
                  Platba bankovým prevodom
                </h2>
                <p className="text-xs text-gray-500 mt-0.5">
                  Zabezpečené cez Stripe
                </p>
              </div>
              <button
                type="button"
                onClick={() => setBankTransferConfirm({ show: false, url: '' })}
                className="flex-shrink-0 -mr-2 -mt-1 p-2 text-gray-400 hover:text-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                aria-label="Zavrieť"
                data-testid="bank-transfer-modal-close"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Body */}
            <div className="px-5 py-4 sm:px-6 sm:py-5 space-y-4 text-sm text-gray-700">
              <p>
                Po pokračovaní sa otvorí zabezpečená platobná stránka Stripe v novej karte.
                Stripe vám zobrazí údaje potrebné na bankový prevod – sumu, IBAN, BIC a referenciu platby.
              </p>

              {/* Numbered 4-step guide */}
              <ol className="space-y-3" data-testid="bank-transfer-steps">
                {[
                  'Otvorte zabezpečenú platobnú stránku Stripe.',
                  'Stripe vám zobrazí IBAN, BIC, sumu a referenciu platby.',
                  'Podľa týchto údajov odošlite platbu vo svojom internetbankingu alebo mobilnej bankovej aplikácii.',
                  'Po prijatí platby Stripe automaticky aktivuje váš prístup v EduMisií.',
                ].map((text, i) => (
                  <li key={i} className="flex items-start gap-3" data-testid={`bank-transfer-step-${i + 1}`}>
                    <span className="flex-shrink-0 w-7 h-7 rounded-full bg-blue-600 text-white text-sm font-bold flex items-center justify-center">
                      {i + 1}
                    </span>
                    <span className="pt-0.5 leading-snug">{text}</span>
                  </li>
                ))}
              </ol>

              {/* Warning / clarification about non-SK bank country */}
              <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-3">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 flex-shrink-0 text-amber-600 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <p className="text-xs text-amber-900">
                  <strong>Upozornenie:</strong> Bankový účet zobrazený Stripe môže byť vedený v inej krajine EÚ, napríklad v Nemecku.
                  Ide o účet platobnej služby Stripe určený na prijatie vašej platby. Nejde o účet EduMisie.
                </p>
              </div>

              <p className="text-xs text-gray-600">
                Po odoslaní bankového prevodu nemusíte platbu v EduMisií ďalej potvrdzovať.
                Po prijatí platby Stripe automaticky informuje EduMisiu a váš prístup sa aktivuje.
              </p>
            </div>

            {/* Footer / CTA */}
            <div className="px-5 py-4 sm:px-6 sm:py-5 border-t border-gray-100 bg-gray-50 rounded-b-2xl space-y-2">
              <a
                href={bankTransferConfirm.url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => {
                  // Close the modal on the SAME tick as the user's click.
                  // Because the <a target="_blank"> is the direct target
                  // of the user gesture, no popup blocker intercepts the
                  // new tab.
                  setBankTransferConfirm({ show: false, url: '' })
                }}
                className="block w-full text-center bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-4 rounded-lg transition inline-flex items-center justify-center gap-2"
                data-testid="bank-transfer-modal-continue"
              >
                <span>Pokračovať na platbu</span>
                <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </a>
              <p className="text-xs text-gray-500 text-center">
                Platobná stránka Stripe sa otvorí v novej karte.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Pricing
